import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import styles from './AddBlockedDomainDialog.module.scss'

import { isApiError } from '@/api'
import { addBlockedDomain } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormField from '@/components/FormField/FormField'
import { BLOCKED_DOMAINS_QUERY_KEY } from '@/pages/app/BlockedDomains'

const blockedDomainSchema = z.object({
  domain: z.string().min(1, 'Domain is required'),
  notes: z.string().min(1, 'Notes is required'),
})

type BlockedDomainFormValues = z.infer<typeof blockedDomainSchema>

interface AddBlockedDomainDialogProps {
  isOpen: boolean
  onSave: () => void
  onCancel: () => void
}

const AddBlockedDomainDialog = ({ isOpen, onSave, onCancel }: AddBlockedDomainDialogProps) => {
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const queryClient = useQueryClient()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BlockedDomainFormValues>({
    resolver: zodResolver(blockedDomainSchema),
    defaultValues: { domain: '', notes: '' },
  })

  const addMutation = useMutation({
    mutationFn: (values: BlockedDomainFormValues) =>
      addBlockedDomain({
        domain: values.domain,
        notes: values.notes,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: BLOCKED_DOMAINS_QUERY_KEY })
      if (response.data.warning) {
        setWarning(response.data.warning)
        setIsSuccess(true)
      } else {
        reset()
        onSave()
      }
    },
    onError: (err) => {
      if (isApiError(err)) {
        setError(err.detail || 'Failed to add blocked domain')
      } else {
        setError('Failed to add blocked domain')
      }
    },
  })

  const handleCancel = () => {
    setError(null)
    setWarning(null)
    setIsSuccess(false)
    reset()
    onCancel()
  }

  const handleClose = () => {
    setError(null)
    setWarning(null)
    setIsSuccess(false)
    reset()
    onSave()
  }

  const onSubmit = (values: BlockedDomainFormValues) => {
    setError(null)
    setWarning(null)
    addMutation.mutate(values)
  }

  return (
    <DialogWrapper isOpen={isOpen} title="Add Blocked Domain" close={handleCancel} minWidth={500}>
      <form className={styles.dialogBody} onSubmit={handleSubmit(onSubmit)}>
        {error && <div className={styles.errorBanner}>{error}</div>}
        {warning && <div className={styles.warningBanner}>{warning}</div>}

        <FormField
          name="domain"
          label="Domain"
          control={control}
          error={errors.domain}
          placeholder="example.com"
          disabled={isSuccess}
        />

        <FormField
          name="notes"
          type="textarea"
          label="Notes"
          control={control}
          error={errors.notes}
          placeholder="Reason for blocking this domain..."
          disabled={isSuccess}
        />

        <div className={styles.dialogFooter}>
          {isSuccess ? (
            <Button title="Close" onClick={handleClose} type="dark" />
          ) : (
            <>
              <Button title="Cancel" onClick={handleCancel} type="light" />
              <Button
                title={addMutation.isPending ? 'Adding...' : 'Add Domain'}
                onClick={handleSubmit(onSubmit)}
                type="dark"
                isDisabled={addMutation.isPending}
              />
            </>
          )}
        </div>
      </form>
    </DialogWrapper>
  )
}

export default AddBlockedDomainDialog
